import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { SECTIONS, STEPS } from '../demo/steps';
import { useVisited } from '../demo/useVisited';

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/*
 * A modal list of every step. It only mounts while open, so its
 * `shell.contents-<screen ID, dots as dashes>` IDs (e.g.
 * `shell.contents-product-trends`) never coexist with a closed copy. They name
 * the screen, not its position, so adding a step never renames them.
 */
export default function ContentsDrawer({ currentStep, onClose, toggleRef }) {
  const { visited } = useVisited();
  const panelRef = useRef(null);

  useEffect(() => {
    const panel = panelRef.current;
    const start = panel.querySelector('[aria-current="step"]') || panel.querySelector(FOCUSABLE);
    start?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    const onPointerDown = (e) => {
      if (panel.contains(e.target) || toggleRef.current?.contains(e.target)) return;
      onClose();
    };

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose, toggleRef]);

  return (
    <>
      <div className="drawer-scrim" aria-hidden="true" />
      <div
        ref={panelRef}
        id="contents-drawer"
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contents-drawer-title"
        data-trapezium-state="open"
      >
        <div className="drawer__head">
          <h2 id="contents-drawer-title" className="drawer__title">
            Contents
          </h2>
          <button
            type="button"
            className="icon-btn"
            aria-label="Close contents"
            data-trapezium-id="shell.close-contents"
            onClick={onClose}
          >
            <X aria-hidden="true" size={20} />
          </button>
        </div>

        <nav className="drawer__body" aria-label="All steps">
          {SECTIONS.map((section) => (
            <section key={section.key} className="drawer__section">
              <h3 className="drawer__section-title">{section.label}</h3>
              <ol className="drawer__steps">
                {STEPS.filter((s) => s.section === section.key).map((s) => {
                  const isCurrent = currentStep?.n === s.n;
                  const isVisited = visited.has(s.route);
                  return (
                    <li key={s.n}>
                      <Link
                        to={s.route}
                        className={isCurrent ? 'drawer__step is-current' : 'drawer__step'}
                        aria-current={isCurrent ? 'step' : undefined}
                        aria-label={`Step ${s.n}: ${s.title}${isVisited ? ', visited' : ''}`}
                        data-trapezium-id={`shell.contents-${s.screenId.replace(/\./g, '-')}`}
                        onClick={onClose}
                      >
                        <span className="drawer__num" aria-hidden="true">
                          {s.n}
                        </span>
                        <span className="drawer__name">{s.title}</span>
                        {isVisited && <Check className="drawer__tick" aria-hidden="true" size={16} />}
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </nav>
      </div>
    </>
  );
}
